import replyIcon from "@loot-os/icons/icons/reply";
import type { ReactNode } from "react";
import { Avatar } from "../../core/Avatar.js";
import { Badge } from "../../core/Badge.js";
import { Icon } from "../../core/Icon.js";
import { cn } from "../../foundations/cn.js";
import { FOCUS_RING } from "../../foundations/interaction.js";
import { useLayout } from "../../foundations/themeContext.js";
import { LevelBadge } from "../LevelBadge.js";
import {
  CHAT_NAME_TONES,
  type ChatAuthor,
  type ChatRowStyle,
  type ChatSurface,
  chatNameTone,
} from "./chatTypes.js";

export interface ChatMessageProps {
  author: ChatAuthor;
  /** The message as sent; @mentions are picked out. */
  text: string;
  /** When it was sent: `label` is shown ("7:42 PM"), `dateTime` is machine-readable. */
  time?: { label: string; dateTime: string };
  /** Sent by the person reading. */
  mine?: boolean;
  /** Mentions the person reading. */
  mentioned?: boolean;
  /** Removed by its author or a moderator: `label` ("Message deleted") replaces the text. */
  deleted?: { label: string };
  /** The message this one answers. With `onJump`, the quote scrolls to it. */
  replyTo?: { name: string; text: string; onJump?: () => void; jumpLabel?: string };
  /** ChatReactions, under the text. */
  reactions?: ReactNode;
  /** ChatMessageActions: shown on hover, on focus within the row, or while `selected`. */
  actions?: ReactNode;
  /** Holds the actions open: a touch selection, or their menu is open. */
  selected?: boolean;
  /** Opens the author's profile from their name. */
  onAuthorClick?: () => void;
  /** Overrides the theme's row style (`layout.chatRows`). */
  rowStyle?: ChatRowStyle;
  surface?: ChatSurface;
  className?: string;
}

/** The message's words, with each @mention picked out. */
export function ChatText({ text }: { text: string }) {
  return text.split(/(@[\w.]+)/g).map((part, i) =>
    part.startsWith("@") ? (
      // Split keeps captures at odd indexes, so the index is stable for a given text.
      // biome-ignore lint/suspicious/noArrayIndexKey: parts of one immutable string
      <span key={i} className="font-medium text-accent-text">
        {part}
      </span>
    ) : (
      part
    ),
  );
}

function AuthorName({ author, onClick }: { author: ChatAuthor; onClick?: () => void }) {
  const tone =
    author.role?.kind === "host"
      ? "text-accent-text"
      : CHAT_NAME_TONES[author.nameTone ?? chatNameTone(author.name)];
  const className = cn("font-semibold", tone);
  return onClick ? (
    <button
      type="button"
      onClick={onClick}
      className={cn(className, "cursor-pointer rounded-xs hover:underline", FOCUS_RING)}
    >
      {author.name}
    </button>
  ) : (
    <span className={className}>{author.name}</span>
  );
}

function Marks({ author }: { author: ChatAuthor }) {
  if (!author.level && !author.role) return null;
  return (
    <span className="mr-1 inline-flex translate-y-0.5 items-center gap-1 align-baseline">
      {author.level && <LevelBadge {...author.level} />}
      {author.role && (
        <Badge
          tone={author.role.kind === "host" ? "solid" : "accent"}
          className="rounded-xs px-1 py-0 font-mono text-mono-meta"
        >
          {author.role.label}
        </Badge>
      )}
    </span>
  );
}

function ReplyQuote({ replyTo }: { replyTo: NonNullable<ChatMessageProps["replyTo"]> }) {
  const content = (
    <>
      {/* Read before the quote, so the visible words stay part of the button's name. */}
      {replyTo.onJump && replyTo.jumpLabel && (
        <span className="sr-only">{replyTo.jumpLabel}: </span>
      )}
      <Icon icon={replyIcon} size={12} className="shrink-0" />
      <span className="font-medium text-text-secondary">{replyTo.name}</span>
      <span className="truncate">{replyTo.text}</span>
    </>
  );
  const className = "flex max-w-full items-center gap-1 font-body text-body-b3 text-text-secondary";
  return replyTo.onJump ? (
    <button
      type="button"
      onClick={replyTo.onJump}
      className={cn(className, "cursor-pointer rounded-xs hover:text-text-primary", FOCUS_RING)}
    >
      {content}
    </button>
  ) : (
    <p className={className}>{content}</p>
  );
}

const ROW_ACTIVE =
  "group-hover:opacity-100 group-focus-within:opacity-100 group-data-selected:opacity-100";

/**
 * One message in a chat. Stream rows read as one line, the name and text inline; card rows are
 * a filled block with the name above. A host's row is marked with an accent edge, and so is one
 * that mentions the reader.
 */
export function ChatMessage({
  author,
  text,
  time,
  mine = false,
  mentioned = false,
  deleted,
  replyTo,
  reactions,
  actions,
  selected = false,
  onAuthorClick,
  rowStyle,
  surface = "panel",
  className,
}: ChatMessageProps) {
  const layout = useLayout();
  const style = rowStyle ?? layout.chatRows;
  const host = author.role?.kind === "host";
  const marked = host || mentioned;
  const overlay = surface === "overlay";

  const body = deleted ? (
    <span className="text-text-secondary italic">{deleted.label}</span>
  ) : (
    <span
      className={cn(
        overlay ? "font-medium text-text-primary" : "text-text-primary",
        mine && "font-medium",
      )}
    >
      <ChatText text={text} />
    </span>
  );
  const timeEl = time && (
    <time
      dateTime={time.dateTime}
      className="font-mono text-mono-meta whitespace-nowrap text-text-secondary"
    >
      {time.label}
    </time>
  );

  return (
    <div
      data-selected={selected || undefined}
      className={cn(
        "group relative flex font-body text-body-b2",
        marked && "border-l-2",
        host ? "border-accent-default" : mentioned && "border-accent-highlight",
        style === "stream"
          ? cn(
              "gap-1.5 rounded-sm px-2 py-1",
              marked && "bg-accent-subtle",
              overlay
                ? "hover:bg-bg-canvas/80 focus-within:bg-bg-canvas/80 data-selected:bg-bg-canvas/80"
                : "hover:bg-bg-raised focus-within:bg-bg-raised data-selected:bg-bg-raised",
            )
          : cn(
              "gap-2.5 rounded-md p-2.5",
              marked ? "bg-accent-subtle" : mine ? "bg-bg-hover" : "bg-bg-raised",
            ),
        className,
      )}
    >
      <Avatar
        src={author.avatarSrc}
        name={author.name}
        size={style === "stream" ? "xs" : "sm"}
        decorative
        className={style === "stream" ? "mt-px" : "size-8"}
      />
      {style === "stream" ? (
        <div className="flex min-w-0 flex-1 flex-col gap-0.5 leading-5">
          {replyTo && <ReplyQuote replyTo={replyTo} />}
          <p className="break-words">
            <Marks author={author} />
            <AuthorName author={author} onClick={onAuthorClick} />
            <span className="text-text-secondary">: </span>
            {body}
          </p>
          {reactions}
        </div>
      ) : (
        <div className="flex min-w-0 flex-1 flex-col gap-1">
          {replyTo && <ReplyQuote replyTo={replyTo} />}
          <p className="flex min-w-0 items-center gap-1.5 text-body-b3">
            <Marks author={author} />
            <AuthorName author={author} onClick={onAuthorClick} />
            <span className="sr-only">: </span>
            {timeEl && <span className="ml-auto">{timeEl}</span>}
          </p>
          <p className="break-words leading-5">{body}</p>
          {reactions}
        </div>
      )}
      {(actions || (style === "stream" && timeEl)) && (
        // Hover's controls float over the row's top right, so the text never reflows.
        <div
          className={cn(
            "absolute -top-3 right-1 z-10 flex items-center gap-2 opacity-0 transition-opacity duration-fast ease-standard motion-reduce:transition-none",
            "pointer-events-none group-hover:pointer-events-auto group-focus-within:pointer-events-auto group-data-selected:pointer-events-auto",
            ROW_ACTIVE,
          )}
        >
          {style === "stream" && timeEl && (
            <span className="rounded-xs bg-bg-raised px-1.5 py-0.5 shadow-float">{timeEl}</span>
          )}
          {actions}
        </div>
      )}
    </div>
  );
}
